<template>
    <div>
        <Loading page v-if="loading" />

        <div class="mb-3 audit-button-container" v-if="!loading">
            <div class="float-left">
                <button type="button" 
                    class="btn btn-primary"
                    @click="showFilterModal()">
                    Filtro
                </button>
            </div>

            <slot name="auditToggle" />
        </div>

        <FilterInformationCmp 
            v-if="!loading"
            :filter-submitted="auditInstanceFilterSubmitted"
            :filter-properties="auditInstanceFilterProperties" />

        <div class="modal" tabindex="-1" role="dialog" id="muven-audit-instance-additional-data-modal">
            <div class="modal-dialog modal-lg" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Dados trafegados</h5>
                        <p class="modal-subtitle">{{audit.description}} - {{audit.date | dateTimeFilter}}</p>
                    </div>

                    <div class="modal-body">
                        <div v-show="audit.hasAdditionalData()">                        
                            <tree-view 
                                :deep="4"
                                :highlight-mouseover-node="true"
                                :show-length="true"
                                :data="audit.additionalData" />
                        </div>

                        <div v-show="!audit.hasAdditionalData()">
                            <p class="text-muted">Dados removidos devido ao tempo de expiração da auditoria.</p>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button 
                            type="button"
                            class="btn btn-outline-secondary"
                            @click="downloadAuditHandler(audit)">
                            Baixar
                        </button>

                        <button type="button" 
                            class="btn btn-outline-secondary" 
                            @click="closeAdditionalDataModal()">                                    
                            Fechar
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div class="modal" tabindex="-1" role="dialog" id="muven-audit-instance-list-filter-modal">
            <div class="modal-dialog modal-lg" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Filtro de auditoria</h5>
                    </div>
                    <div class="modal-body">
                        <div>
                            <div class="row">
                                <div class="col-12 col-md-4">
                                    <div class="form-group">
                                        <label>Data inicial</label>
                                        <v-date-picker 
                                            :input-props="{'class': 'form-control'}" 
                                            popover-visibility="focus"
                                            v-model="auditInstanceFilter.date.start" />
                                    </div>
                                </div>

                                <div class="col-12 col-md-4">
                                    <div class="form-group">
                                        <label>Data final</label>
                                        <v-date-picker 
                                            :input-props="{'class': 'form-control'}" 
                                            popover-visibility="focus"
                                            v-model="auditInstanceFilter.date.end" />
                                    </div>
                                </div>
                            </div>

                            <div class="row">
                                <div class="col-12 col-md-6 form-group">
                                    <label>Entidades</label>
                                    <multiselect
                                        v-model="auditInstanceFilter.entityList"
                                        :options="entityListOptions"
                                        :multiple="true"
                                        :show-labels="false"
                                        label="description"
                                        track-by="enum"
                                        placeholder="" />
                                </div>

                                <div class="col-12 col-md-6 form-group">
                                    <label>Status</label>
                                    <multiselect
                                        v-model="auditInstanceFilter.statusList"
                                        :options="statusListOptions"
                                        :multiple="true"
                                        :show-labels="false"
                                        label="description"
                                        track-by="enum"
                                        placeholder="" />
                                </div>
                            </div>

                            <div class="row">
                                <div class="col-12 col-md-6 form-group">
                                    <label>Modificado por</label>
                                    <multiselect
                                        v-model="auditInstanceFilter.modificationSourceList"
                                        :options="modificationSourceListOptions"
                                        :multiple="true"
                                        :show-labels="false"
                                        label="description"
                                        track-by="enum"
                                        placeholder="">
                                        <template slot="option" slot-scope="{ option }">
                                            <span class="audit-filter-option">
                                                <AuditActorTileCmp :kind="actorKindBySource(option.enum)" :size="18" />
                                                <span>{{option.description}}</span>
                                            </span>
                                        </template>
                                        <template slot="tag" slot-scope="{ option, remove }">
                                            <span class="multiselect__tag audit-filter-tag">
                                                <AuditActorTileCmp :kind="actorKindBySource(option.enum)" :size="14" />
                                                <span>{{option.description}}</span>
                                                <i aria-hidden="true" tabindex="1" class="multiselect__tag-icon"
                                                    @keypress.enter.prevent="remove(option)"
                                                    @mousedown.prevent="remove(option)"></i>
                                            </span>
                                        </template>
                                    </multiselect>
                                </div>

                                <div class="col-12 col-md-6 form-group">
                                    <label>Canal</label>
                                    <multiselect
                                        v-model="auditInstanceFilter.channelList"
                                        :options="channelListOptions"
                                        :multiple="true"
                                        :show-labels="false"
                                        label="name"
                                        track-by="id"
                                        placeholder="">
                                        <template slot="option" slot-scope="{ option }">
                                            <ChannelLogoCmp v-if="channelById(option.id)" :channel="channelById(option.id)"
                                                :size="18" :label="option.name" />
                                            <span v-else>{{option.name}}</span>
                                        </template>
                                        <template slot="tag" slot-scope="{ option, remove }">
                                            <span class="multiselect__tag audit-filter-tag">
                                                <ChannelLogoCmp v-if="channelById(option.id)" :channel="channelById(option.id)"
                                                    :size="14" :show-name="false" />
                                                <span>{{option.name}}</span>
                                                <i aria-hidden="true" tabindex="1" class="multiselect__tag-icon"
                                                    @keypress.enter.prevent="remove(option)"
                                                    @mousedown.prevent="remove(option)"></i>
                                            </span>
                                        </template>
                                    </multiselect>
                                </div>
                            </div>

                            <div class="row">
                                <div class="col-12 col-md-6 form-group">
                                    <label>Email do usuário</label>
                                    <input type="text" 
                                        class="form-control"
                                        v-model="auditInstanceFilter.userEmail" />
                                </div>
                            </div>

                            <div class="row">
                                <div class="col-12 form-group">
                                    <label>Descrição</label>
                                    <input type="text" 
                                        class="form-control"
                                        v-model="auditInstanceFilter.description" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" 
                            class="btn btn-primary" 
                            @click="getAuditListWithFilter(auditInstanceFilter)"
                            :disabled="loadingFilter">                                    
                                Filtrar
                                <Loading v-show="loadingFilter" />
                        </button>

                        <button type="button" 
                            class="btn btn-outline-secondary" 
                            @click="clearFilter()">Restaurar</button>

                        <button type="button" 
                            class="btn btn-outline-secondary" 
                            @click="closeFilterModal()">Fechar</button>
                    </div>
                </div>
            </div>
        </div>

        <NothingFound v-if="nothingToShow" />

        <div v-if="!loading && !nothingToShow">
            <div class="table-responsive">
                <table class="table table-hover table-sm table-striped">
                    <thead>
                        <tr>
                            <th>Entidade</th>
                            <th>Descrição</th>
                            <th class="interaction-col">Interação</th>
                            <th class="datetime-col">Data e hora</th>
                            <th class="status-col">Status</th>
                            <th></th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr v-for="(audit, index) in auditList" :key="index">
                            <td>{{audit.getEntityEnumDescription()}}</td>
                            <td>{{audit.description}}</td>
                            <td><AuditActorCmp :audit="audit" :subscriber="subscriber" /></td>
                            <td>{{audit.date | dateTimeFilter}}</td>
                            <td>
                                <span class="muven-td-icon">
                                    <fa icon="hourglass-start" 
                                        v-if="audit.isStatusRunning()"
                                        class="text-muted" />

                                    <fa icon="check-circle" 
                                        v-if="audit.isStatusCompleted()"
                                        class="text-success" />

                                    <fa icon="exclamation-triangle" 
                                        v-if="audit.isStatusError()"
                                        class="text-danger" />

                                    <fa icon="exclamation-triangle"
                                        v-if="audit.isStatusWarning()"
                                        class="text-warning" />
                                </span>

                                <span>{{audit.getStatusEnumDescription()}}</span>
                            </td>
                            <td class="text-center muven-td-with-buttons">
                                <button type="button" 
                                    class="btn btn-default text-muted" 
                                    data-toggle="dropdown" >
                                    <fa icon="ellipsis-v" v-show="!audit.loadingListAction" />
                                    <fa icon="sync-alt" spin v-show="audit.loadingListAction" />
                                </button>

                                <div class="dropdown-menu dropdown-menu-right muven-action-menu">
                                    <router-link class="dropdown-item"
                                        :to="detailUrl(audit)"
                                        target="_blank"
                                        rel="noopener"
                                        title="Abre em uma nova aba">
                                        <fa icon="file-alt" fixed-width class="muven-action-icon" />
                                        <span class="muven-action-label">Detalhes</span>
                                        <fa icon="external-link-alt" class="muven-action-external" />
                                    </router-link>

                                    <span class="dropdown-item"
                                        @click="showAdditionalDataModal(audit)">
                                        <fa icon="eye" fixed-width class="muven-action-icon" />
                                        <span class="muven-action-label">Dados trafegados</span>
                                    </span>

                                    <div v-show="audit.hasIdEntity()">
                                        <p class="dropdown-divider"></p>

                                        <a class="dropdown-item"
                                            :href="getEntityUrl(audit.idEntity, audit.entity)"
                                            target="_blank"
                                            rel="noopener"
                                            title="Abre em uma nova aba">
                                            <fa icon="link" fixed-width class="muven-action-icon" />
                                            <span class="muven-action-label">Ir para entidade</span>
                                            <fa icon="external-link-alt" class="muven-action-external" />
                                        </a>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <ListCount :amount="auditList.length" />
            </div>

            <div v-if="showPaginationButton" class="text-center">
                <button class="btn btn-outline-secondary"
                    @click="getMoreAuditList()"
                    :disabled="loadingNextPage">
                    <span v-show="!loadingNextPage">Mais resultados</span>
                    <fa v-show="loadingNextPage" icon="sync-alt" spin/>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import {cloneDeep, uniqBy} from 'lodash'
import {dateTimeFilter} from '@/utils/filters/DateTimeFilter'
import {AuditInstanceModel} from '@/audit/models/AuditInstanceModel'
import {toQueryString} from '@/utils/parsers/QueryString'
import {PaginationModel} from '@/utils/models/PaginationModel'
import auditService from '@/audit/services/AuditService'
import {AuditInstanceListFilterModel} from '@/audit/models/AuditInstanceListFilterModel'
import modalService from '@/utils/services/ModalService'
import FilterInformationCmp from '@/utils/components/FilterInformationCmp'
import GetEntityUrl from '@/utils/mixins/GetEntityUrl'
import ChannelService from '@/channel/services/ChannelService'
import VueJsonPretty from 'vue-json-pretty';
import { mapState } from 'vuex'
import DownloadJson from '../../utils/mixins/DownloadJson'
import AuditActorCmp from '@/audit/components/AuditActorCmp.vue'
import AuditActorTileCmp from '@/audit/components/AuditActorTileCmp.vue'
import ChannelLogoCmp from '@/channel/components/ChannelLogoCmp.vue'
import { getAuditActorKindBySource } from '@/audit/models/AuditActor'

const ID_FILTER_MODAL = '#muven-audit-instance-list-filter-modal'
const ID_ADDITIONAL_DATA_MODAL = '#muven-audit-instance-additional-data-modal'

export default {
    name: 'AuditInstanceListView',
    data() {
        return {
            loading: true,
            loadingNextPage: false,
            loadingFilter: false,
            showPaginationButton: true,
            auditList: [],
            channelList: [],
            audit: new AuditInstanceModel(),
            pagination: new PaginationModel(),
            statusListOptions: AuditInstanceListFilterModel.statusList(),
            entityListOptions: AuditInstanceListFilterModel.entityList(),
            modificationSourceListOptions: AuditInstanceListFilterModel.modificationSourceList(),
            auditInstanceFilter: new AuditInstanceListFilterModel(),
            auditInstanceFilterSubmitted: new AuditInstanceListFilterModel(),
            auditInstanceFilterProperties: [
                {
                    type: 'daterange',
                    label: 'Período',
                    property: 'date',
                },
                {
                    type: 'string',
                    label: 'Id da entidade',
                    property: 'idEntity',
                },
                {
                    type: 'list',
                    label: 'Entidade',
                    property: 'entityList',
                    propertyInList: 'description',
                },
                {
                    type: 'list',
                    label: 'Status',
                    property: 'statusList',
                    propertyInList: 'description',
                },
                {
                    type: 'list',
                    label: 'Modificado por',
                    property: 'modificationSourceList',
                    propertyInList: 'description',
                },
                {
                    type: 'list',
                    label: 'Canal',
                    property: 'channelList',
                    propertyInList: 'name',
                },
                {
                    type: 'string',
                    label: 'Descrição',
                    property: 'description',
                },
                {
                    type: 'string',
                    label: 'Email de usuário',
                    property: 'userEmail',
                },
                {
                    type: 'string',
                    label: 'Id do cliente',
                    property: 'clientId',
                },
            ]
        }
    },
    components: {
        FilterInformationCmp,
        'tree-view': VueJsonPretty,
        AuditActorCmp,
        AuditActorTileCmp,
        ChannelLogoCmp,
    },
    filters: {
        dateTimeFilter,        
    },
    mixins: [
        GetEntityUrl,
        DownloadJson,
    ],
    computed: {
        ...mapState({
            subscriber: state => state.subscriber,
        }),
        nothingToShow() {
            return !this.loading && !this.auditList.length
        },
        detailUrl() {
            return (audit) => {
                return `/auditorias/${audit._id}`
            }
        },
        channelListOptions() {
            return this.channelList.map(channel => ({
                id: parseInt(channel.id, 10),
                name: channel.name,
            }))
        },
    },
    methods: {
        goto(audit) {
            this.$router.push(`/auditorias/${audit._id}`);
        },
        downloadAuditHandler(audit) {
            this.downloadJSON(audit, `Auditoria ${audit.getEntityEnumDescription()} - ${audit.date.toISOString()}.json`)
        },
        async getMoreAuditList() {
            try {
                this.loadingNextPage = true
                const filter = this._serializeFilter(this.auditInstanceFilter)
                const newAuditList = await auditService.getInstances(this.pagination.actual(), filter)
                this.auditList.push(...newAuditList)

                this._updateChannelNames()

                if (!newAuditList.length) {
                    this.showPaginationButton = false
                }

                this.pagination.next()
            } catch(e) {
                this.$floatingAlert.error({
                    message: e.message,
                })
            }

            this.loadingNextPage = false
        },
        async getAuditListWithFilter(filterList) {
            this.showPaginationButton = true
            this.auditInstanceFilterSubmitted = cloneDeep(filterList)
            this.pagination.reset()
            this.loadingFilter = true
            try {
                const filter = this._serializeFilter(filterList)
                this.auditList = await auditService.getInstances(this.pagination.actual(), filter)
                this.pagination.next()                
                this._updateChannelNames()
                this.closeFilterModal()
            } catch(e) {
                this.$floatingAlert.error({
                    message: e.message,
                })
            }

            this.loadingFilter = false
        },
        // Converte channelList de objetos para array de inteiros antes de enviar
        _serializeFilter(filterList) {
            const serialized = cloneDeep(filterList)
            if (serialized.channelList && serialized.channelList.length) {
                serialized.channelList = serialized.channelList.map(c => parseInt(c.id, 10))
            }
            return serialized
        },
        async showFilterModal() {
            await modalService.show(ID_FILTER_MODAL)
        },
        async closeFilterModal() {
            await modalService.hide(ID_FILTER_MODAL)
        },
        clearFilter() {
            this.auditInstanceFilter = new AuditInstanceListFilterModel()
        },
        async showAdditionalDataModal(audit) {
            audit.loadingListAction = true
            try {
                this.audit = await auditService.getInstanceById(audit._id)
                await modalService.show(ID_ADDITIONAL_DATA_MODAL)
            } catch (err) {
                this.$floatingAlert.error({
                    message: err.message,
                })
            }
            audit.loadingListAction = false
        },
        async closeAdditionalDataModal() {
            await modalService.hide(ID_ADDITIONAL_DATA_MODAL)
        },
        actorKindBySource(source) {
            return getAuditActorKindBySource(source)
        },
        // As opções do filtro só têm id e nome; a marca vem do canal completo.
        channelById(id) {
            return this.channelList.find(c => parseInt(c.id, 10) === parseInt(id, 10))
        },
        async _updateChannelNames() {
            this.auditList.forEach((audit) => {
                for (const channel of this.channelList) {
                    if (parseInt(audit.idChannel, 10) === parseInt(channel.id, 10)) {
                        audit.setChannel(channel)
                    }
                }
            })
        },
    },
    async mounted() {
        try {
            const querystring = this.$route.query
            this.auditInstanceFilter = new AuditInstanceListFilterModel(querystring)
            this.channelList = await ChannelService.getChannels()
            const filter = this._serializeFilter(this.auditInstanceFilter)
            this.auditList = await auditService.getInstances(this.pagination.actual(), filter)
            this.pagination.next()

            this._updateChannelNames()

            if (querystring) {                
                this.auditInstanceFilterSubmitted = cloneDeep(this.auditInstanceFilter)
            }
        } catch(e) {
            this.$floatingAlert.error({
                message: e.message,
            })
        }

        this.loading = false
    },
}
</script>

<style lang="scss" scoped>
#muven-audit-instance-additional-data-modal {
    .modal-body {
        max-height: calc(80vh - 100px);
        overflow: auto;
    }
}

.audit-filter-option,
.audit-filter-tag {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.audit-filter-tag {
    gap: 6px;
}

.audit-button-container {
    overflow: hidden;
}
</style>